import { currentUser } from '@easy-expense/auth-client';
import { useWorkspaceKeysStore } from '@easy-expense/data-firestore-client';
import { WebEnv } from '@easy-expense/env-mobile';
import { DoNotFixMeIAmAny } from '@easy-expense/utils-typescript';
import React from 'react';

export function useFreshchat() {
  const user = currentUser();
  const currentWorkspacePath = useWorkspaceKeysStore((s) => s.currentWorkspacePath());
  const isOnboarding = window.location.pathname.includes('welcome');

  React.useEffect(() => {
    if (isOnboarding) {
      return;
    }

    const fcWindow = window as DoNotFixMeIAmAny;

    const safeCall = (fn: () => void, context: string) => {
      try {
        fn();
      } catch (err) {
        console.warn(`[Freshchat] Error in ${context}:`, err);
      }
    };

    const setUserProperties = () => {
      if (!fcWindow?.fcWidget) {
        return;
      }

      safeCall(() => fcWindow.fcWidget.setExternalId(user?.uid), 'setExternalId');
      safeCall(() => fcWindow.fcWidget.user.setFirstName(user?.displayName), 'setFirstName');
      safeCall(() => fcWindow.fcWidget.user.setEmail(user?.email), 'setEmail');

      if (currentWorkspacePath) {
        const encodedPath = currentWorkspacePath.replace(/\//g, '~2F');
        const firestoreUrl = `https://console.firebase.google.com/project/${WebEnv.firebase.projectId}/firestore/data/~2F${encodedPath}`;
        safeCall(() => {
          fcWindow.fcWidget.user.setProperties({ workspacePath: firestoreUrl });
        }, 'setUserProperties');
      }
    };

    const initFreshchatWidget = () => {
      if (!fcWindow?.fcWidget) {
        return;
      }

      safeCall(() => fcWindow.bob?.(), 'bob (custom init?)');

      safeCall(() => {
        fcWindow.fcWidget.init({
          token: '5cf30597-2420-48d0-843a-9e4b473a8ba2',
          host: 'https://easyexpense-team.freshchat.com',
        });
      }, 'fcWidget.init');

      setUserProperties();
    };

    const loadFreshchatScript = () => {
      const scriptId = 'Freshchat-js-sdk';
      const existingScript = document.getElementById(scriptId);

      if (existingScript) {
        safeCall(initFreshchatWidget, 'initFreshchatWidget (existing script)');
        return;
      }

      try {
        const script = document.createElement('script');
        script.id = scriptId;
        script.async = true;
        script.src = 'https://easyexpense-team.freshchat.com/js/widget.js';
        script.onload = () => safeCall(initFreshchatWidget, 'initFreshchatWidget (script.onload)');

        document.head.appendChild(script);
      } catch (err) {
        console.error('[Freshchat] Error injecting script:', err);
      }
    };

    const updateWidgetIfLoaded = () => {
      if (document.getElementById('Freshchat-js-sdk')) {
        safeCall(setUserProperties, 'setUserProperties (after load)');
      }
    };

    window.addEventListener('load', loadFreshchatScript);
    safeCall(updateWidgetIfLoaded, 'updateWidgetIfLoaded');

    return () => {
      window.removeEventListener('load', loadFreshchatScript);
    };
  }, [user?.uid, user?.displayName, user?.email, currentWorkspacePath, isOnboarding]);
}
